import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Anchor, X, Loader2, RotateCcw, Check } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { isIOSNative, isNativeWithIAP } from "@/lib/platform";
import {
    PAYWALL_OPEN_EVENT,
    configurePurchases,
    getPlanPackages,
    purchase,
    restore,
    hasProEntitlement,
} from "@/lib/iap";

/**
 * NativePaywall — the iOS StoreKit paywall sheet (RevenueCat).
 *
 * Rendered once globally (App.js) and opened from anywhere via openPaywall().
 * iOS native only. Prices come from StoreKit (localized by Apple).
 */
export default function NativePaywall() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [pkgs, setPkgs] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(null); // "monthly" | "annual" | "restore"
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");

    // Identify the captain to RevenueCat as soon as we know who they are.
    // Passing the full user object populates `$email` / `$displayName` on the
    // RC dashboard so support can find customers by name/email.
    useEffect(() => {
        if (user?.id) configurePurchases(user);
    }, [user]);

    useEffect(() => {
        const onOpen = () => setOpen(true);
        window.addEventListener(PAYWALL_OPEN_EVENT, onOpen);
        return () => window.removeEventListener(PAYWALL_OPEN_EVENT, onOpen);
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const result = await getPlanPackages();
            if (!result.monthly && !result.annual) {
                setError("Plans couldn't be loaded. Please check your connection and try again.");
            }
            setPkgs(result);
        } catch (e) {
            console.error("[paywall] offerings failed", e);
            const detail = e?.message ? ` (${e.message})` : "";
            setError(`Plans couldn't be loaded. Please check your connection and try again.${detail}`);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) load();
    }, [open, load]);

    if (!isNativeWithIAP() || !open) return null;

    const finishSubscribed = async () => {
        const syncEndpoint = isIOSNative() ? "/billing/apple/sync" : "/billing/play/sync";
        try { await api.post(syncEndpoint); } catch { /* webhook will catch up */ }
        window.location.reload();
    };

    const buy = async (key) => {
        const pkg = pkgs?.[key];
        if (!pkg) return;
        setBusy(key);
        setError("");
        try {
            const customerInfo = await purchase(pkg);
            if (hasProEntitlement(customerInfo)) {
                await finishSubscribed();
                return;
            }
            setError("The purchase didn't activate. Please try Restore Purchases, or contact captain@bosunsmate.app.");
        } catch (e) {
            if (!e?.userCancelled) {
                console.error("[paywall] purchase failed", e);
                const detail = e?.message ? ` (${e.message})` : "";
                setError(`Purchase failed. You weren't charged — please try again.${detail}`);
            }
        } finally {
            setBusy(null);
        }
    };

    const doRestore = async () => {
        setBusy("restore");
        setError("");
        setNotice("");
        try {
            const customerInfo = await restore();
            if (hasProEntitlement(customerInfo)) {
                await finishSubscribed();
                return;
            }
            setNotice("No previous purchases found for this Apple ID.");
        } catch (e) {
            console.error("[paywall] restore failed", e);
            const detail = e?.message ? ` (${e.message})` : "";
            setError(`Restore failed. Please try again.${detail}`);
        } finally {
            setBusy(null);
        }
    };

    const PlanCard = ({ planKey, title, sub, highlight, testid }) => {
        const pkg = pkgs?.[planKey];
        if (!pkg) return null;
        const price = pkg.product?.priceString || pkg.storeProduct?.priceString || "";
        const currency = pkg.product?.currencyCode || pkg.storeProduct?.currencyCode || "";
        const lengthLabel = planKey === "annual" ? "1-year" : "1-month";
        const unit = planKey === "annual" ? "per year" : "per month";
        return (
            <div
                style={{
                    border: `1px solid ${highlight ? "var(--bm-gold-dim)" : "var(--bm-border)"}`,
                    background: highlight ? "rgba(212,181,114,0.06)" : "rgba(255,255,255,0.02)",
                    borderRadius: 14,
                    padding: "18px 16px",
                }}
            >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                    <div>
                        <div style={{ fontSize: 16, fontWeight: 600, color: "var(--bm-text)" }} data-testid={`paywall-${planKey}-title`}>
                            Bosun's Mate Pro — {title}
                        </div>
                        <div style={{ fontSize: 12, color: highlight ? "var(--bm-gold)" : "var(--bm-text-muted)", marginTop: 2 }}>{sub}</div>
                        <div
                            data-testid={`paywall-${planKey}-length`}
                            style={{ fontSize: 12, color: "var(--bm-text-muted)", marginTop: 6 }}
                        >
                            {lengthLabel} auto-renewing subscription
                        </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                        <div className="bm-serif" style={{ fontSize: 24, color: "var(--bm-text)" }} data-testid={`paywall-${planKey}-price`}>{price}</div>
                        <div style={{ fontSize: 11, color: "var(--bm-text-muted)" }}>{currency ? `${currency} ` : ""}{unit}</div>
                    </div>
                </div>
                <button
                    data-testid={testid}
                    className={highlight ? "bm-btn bm-btn-gold" : "bm-btn bm-btn-ghost"}
                    disabled={!!busy}
                    onClick={() => buy(planKey)}
                    style={{ width: "100%", marginTop: 14, padding: "12px 16px", justifyContent: "center", display: "flex", alignItems: "center", gap: 8 }}
                >
                    {busy === planKey ? <Loader2 size={15} className="bm-spin" /> : <Check size={15} />}
                    {busy === planKey ? "Processing…" : `Subscribe ${planKey === "annual" ? "annually" : "monthly"}`}
                </button>
            </div>
        );
    };

    return (
        <div
            data-testid="native-paywall"
            role="dialog"
            aria-modal="true"
            style={{
                position: "fixed", inset: 0, zIndex: 1100,
                background: "rgba(5, 15, 28, 0.85)",
                backdropFilter: "blur(8px)",
                display: "flex", alignItems: "flex-end", justifyContent: "center",
                paddingTop: "max(env(safe-area-inset-top), 20px)",
            }}
            onClick={() => !busy && setOpen(false)}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: "100%", maxWidth: 520, maxHeight: "92%", overflowY: "auto",
                    background: "var(--bm-panel, #0c1828)",
                    border: "1px solid var(--bm-border)",
                    borderRadius: "18px 18px 0 0",
                    padding: "26px 22px",
                    paddingBottom: "max(env(safe-area-inset-bottom), 26px)",
                }}
            >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Anchor size={20} color="var(--bm-gold)" />
                        <h2 className="bm-serif" style={{ fontSize: 24, margin: 0, fontWeight: 500, color: "var(--bm-text)" }}>
                            Choose your plan
                        </h2>
                    </div>
                    <button
                        data-testid="paywall-close-btn"
                        onClick={() => setOpen(false)}
                        aria-label="Close"
                        disabled={!!busy}
                        style={{ background: "transparent", border: "none", color: "var(--bm-text-muted)", cursor: "pointer", padding: 6, display: "flex" }}
                    >
                        <X size={20} />
                    </button>
                </div>
                <p style={{ color: "var(--bm-text-muted)", fontSize: 13, lineHeight: 1.5, margin: "0 0 18px" }}>
                    Unlimited vessels, entries, co-captains, receipts and photos — billed through your{" "}
                    {isIOSNative() ? "Apple\u00a0ID" : "Google\u00a0Play\u00a0account"}.
                </p>

                {error && (
                    <div data-testid="paywall-error" style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 10, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)", color: "var(--bm-bad)", fontSize: 13 }}>
                        {error}
                    </div>
                )}
                {notice && (
                    <div data-testid="paywall-notice" style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 10, background: "rgba(91,185,232,0.08)", border: "1px solid rgba(91,185,232,0.3)", color: "var(--bm-text)", fontSize: 13 }}>
                        {notice}
                    </div>
                )}

                {loading ? (
                    <div data-testid="paywall-loading" style={{ display: "flex", justifyContent: "center", padding: "34px 0", color: "var(--bm-text-muted)" }}>
                        <Loader2 size={22} className="bm-spin" />
                    </div>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                        <PlanCard planKey="annual" title="Annual" sub="Save ~30% — most popular" highlight testid="paywall-annual-btn" />
                        <PlanCard planKey="monthly" title="Monthly" sub="Try the captain's seat" testid="paywall-monthly-btn" />
                    </div>
                )}

                {/* "Free forever" reassurance — dismissing the paywall doesn't
                    lose the captain's data. Sits between paid CTAs and the
                    Restore button so it's obvious there's a real opt-out
                    when the trial ends. */}
                <button
                    data-testid="paywall-stay-free-btn"
                    onClick={() => setOpen(false)}
                    disabled={!!busy}
                    className="bm-btn bm-btn-ghost"
                    style={{
                        width: "100%", marginTop: 14, padding: "12px 16px",
                        fontSize: 13, justifyContent: "center",
                        display: "flex", alignItems: "center", gap: 8,
                        border: "1px dashed var(--bm-border)",
                    }}
                >
                    Stay on Free Plan — 1 Vessel, Unlimited Entries
                </button>
                <p style={{ textAlign: "center", fontSize: 11, color: "var(--bm-text-muted)", margin: "6px 0 0" }}>
                    Free forever. Your data and core logging stay yours.
                </p>

                <button
                    data-testid="paywall-restore-btn"
                    onClick={doRestore}
                    disabled={!!busy}
                    className="bm-btn bm-btn-ghost"
                    style={{ width: "100%", marginTop: 14, padding: "10px 16px", fontSize: 13, justifyContent: "center", display: "flex", alignItems: "center", gap: 8 }}
                >
                    {busy === "restore" ? <Loader2 size={14} className="bm-spin" /> : <RotateCcw size={14} />}
                    Restore Purchases
                </button>

                <p
                    data-testid="paywall-disclosure"
                    style={{ color: "var(--bm-text-muted)", fontSize: 12, lineHeight: 1.55, margin: "16px 0 0" }}
                >
                    <strong style={{ color: "var(--bm-text)" }}>Auto-renewable subscription.</strong>{" "}
                    Payment is charged to your {isIOSNative() ? "Apple\u00a0ID" : "Google\u00a0Play\u00a0account"} at confirmation of purchase. Your subscription
                    automatically renews for the same period (1&nbsp;month or 1&nbsp;year) at the displayed price
                    unless auto-renew is turned off at least 24&nbsp;hours before the end of the current period.
                    You can manage or cancel your subscription anytime in your{" "}
                    {isIOSNative() ? "Apple\u00a0ID Account\u00a0Settings" : "Google\u00a0Play Subscriptions"}
                    &rsaquo; Subscriptions. Any unused portion of a free trial is forfeited when you purchase a
                    subscription.
                </p>
                <p
                    data-testid="paywall-legal-links"
                    style={{ color: "var(--bm-text-muted)", fontSize: 12, lineHeight: 1.55, margin: "10px 0 0" }}
                >
                    <Link to="/terms" className="bm-link" onClick={() => setOpen(false)}>Terms of Use (EULA)</Link>
                    {" · "}
                    <Link to="/privacy" className="bm-link" onClick={() => setOpen(false)}>Privacy Policy</Link>
                    {isIOSNative() && (
                        <>
                            {" · "}
                            <a
                                className="bm-link"
                                href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Apple Standard EULA
                            </a>
                        </>
                    )}
                </p>
            </div>
        </div>
    );
}
